import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('udsAdminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch users');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', full_name: '', password: '' });
    setShowCreateForm(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('udsAdminToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingUser) {
        await axios.put(
          `${process.env.REACT_APP_API_BASEURL}/api/users/${editingUser.user_id}`,
          formData,
          config
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASEURL}/api/users`, formData, config);
      }

      resetForm();
      fetchUsers();
    } catch (err) {
      setError('Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      password: ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;

    try {
      const token = localStorage.getItem('udsAdminToken');
      await axios.delete(`${process.env.REACT_APP_API_BASEURL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="page-shell">
        <div className="card">
          <p>Loading users…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin Users</h1>
          <p className="page-subtitle">Manage dashboard operators with secure credentials.</p>
        </div>
        {!showCreateForm && (
          <button className="button" onClick={() => setShowCreateForm(true)}>
            <i className="uicon-add" /> <span>Create User</span>
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {showCreateForm ? (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">{editingUser ? 'Edit User' : 'Create User'}</h2>
            <button type="button" className="button ghost sm" onClick={resetForm}>
              <i className="uicon-arrow-narrow-left" /> Back to list
            </button>
          </div>

          <form onSubmit={handleSubmit} className="form-stack">
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="user-email">Email</label>
                <div className="form-field-control">
                  <input
                    type="email"
                    id="user-email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="user-full-name">Full Name</label>
                <div className="form-field-control">
                  <input
                    type="text"
                    id="user-full-name"
                    placeholder="Full name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="user-password">Password</label>
                <div className="form-field-control">
                  <input
                    type="password"
                    id="user-password"
                    placeholder={
                      editingUser ? 'New Password (optional)' : 'Password'
                    }
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="button">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="button secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="card table-card">
          <table className="uds-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Full Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.user_id}>
                <td>{user.email}</td>
                <td>{user.full_name}</td>
                <td className="table-actions">
                  <button
                    type="button"
                    className="action-button"
                    data-type="edit"
                    onClick={() => handleEdit(user)}
                    title="Edit user"
                  >
                    <i className="uicon-edit" />
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    data-type="trash"
                    onClick={() => handleDelete(user.user_id)}
                    title="Delete user"
                  >
                    <i className="uicon-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default Users;
